import {Component, Input, OnInit} from '@angular/core';
import {NbDialogRef, NbDialogService} from '@nebular/theme';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {PvRecruitmentService, User} from '../pv-recruitment.service';
import {selectAuthState, UserState} from '../../../shared/store/user/auth.state';
import {GetUser} from '../../../shared/store/user/auth.actions';
import {Store} from '@ngrx/store';
import {ToastHandlerService} from '../../../shared/services/toast-handler.service';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {Candidate, DrivingLicenceType, DrivingLicenseAge} from '../models/Candidate';
import {PvRecruitmentDialogReactivateAccountComponent} from '../pv-recruitment-dialog-reactivate-account/pv-recruitment-dialog-reactivate-account.component';
import {ACCEPTED_VALET_FILE_TYPES} from '../../../shared/constants/constants';
import {
  VALET_DOCUMENT_TYPE,
  ValetDocument
} from '../../../common-modules/common-valet/models/valet-document';
import {ValetProfileService} from '../../../valet/services/valet-profile.service';
import {map} from 'rxjs/operators';
import {
  PvRecruitmentQontoDialogComponent
} from '../pv-recruitment-registration/components/pv-recruitment-registration-form/pv-recruitment-qonto-dialog/pv-recruitment-qonto-dialog.component';

declare const dataLayer: any[];

export interface LicensesTypesGroup {
  name: string,
  types: {
    name: string,
    value: string
  }[]
}

@Component({
    selector: 'pv-pv-recruitment-dialog',
    templateUrl: './pv-recruitment-dialog.component.html',
    styleUrls: ['./pv-recruitment-dialog.component.scss'],
    standalone: false
})
export class PvRecruitmentDialogComponent implements OnInit {
  public form: UntypedFormGroup;
  public candidateUser: any;
  public candidate: Candidate;
  public eligibility: string;
  public isSubmitting = false;
  public showConnectButton = false;
  public licensesTypesGroups: LicensesTypesGroup[];

  public fileName: string | File = '';
  public file: any;
  public acceptedValetFileTypes = ACCEPTED_VALET_FILE_TYPES;
  public dialogStep: number;

  constructor(protected dialogRef: NbDialogRef<PvRecruitmentDialogComponent>,
              private toastService: ToastHandlerService,
              private translateService: TranslateService,
              private store: Store<UserState>,
              private fb: UntypedFormBuilder,
              private recruitmentService: PvRecruitmentService,
              public router: Router,
              private dialogService: NbDialogService,
              private valetDocumentService: ValetProfileService) { }

  ngOnInit(): void {
    this.store.select(selectAuthState).subscribe(({user}) => {
      this.candidateUser = user;
    });
    this.store.dispatch(new GetUser());

    this.dialogStep = 0;

    this.licensesTypesGroups = this.getLicensesTypesGroups();

    this.form = this.fb.group({
      hasAutoentrepreneurStatus: [null, [Validators.required]],
      hasInsurer: [null, [Validators.required]],
      noTVA: [null],
      siret: [''],
      drivingLicensePoints: ['', [Validators.required]],
      drivingLicenseAge: ['', [Validators.required]],
      drivingLicenseType: ['', [Validators.required]],
      availableOnSaturday: [null, [Validators.required]]
    });

    this.form.get('hasAutoentrepreneurStatus').valueChanges.subscribe(value => {
      const siretControl = this.form.get('siret');
      const noTVAControl = this.form.get('noTVA');
      if (value) {
        siretControl.setValidators([
          Validators.required,
          Validators.pattern(/^\d{9}$|^\d{14}$/)
        ]);
        noTVAControl.setValidators([
          Validators.required
        ]);
      } else {
        siretControl.clearValidators();
        noTVAControl.clearValidators();
        siretControl.setValue('');
        noTVAControl.setValue(null);
      }
      siretControl.updateValueAndValidity();
      noTVAControl.updateValueAndValidity();
    });
  }

  public getLicensesTypesGroups(): LicensesTypesGroup[] {
    return [{
      name: this.translateService.instant('recruitment.popup.licenses_types_groups.license_category_a.title'),
      types: [{
        name: this.translateService.instant('recruitment.popup.licenses_types_groups.license_category_a.license_a'),
        value: 'a'
      }, {
        name: this.translateService.instant('recruitment.popup.licenses_types_groups.license_category_a.license_a1'),
        value: 'a1'
      }, {
        name: this.translateService.instant('recruitment.popup.licenses_types_groups.license_category_a.license_a2'),
        value: 'a2',
      }]
    }, {
      name: this.translateService.instant('recruitment.popup.licenses_types_groups.license_category_c_and_d.title'),
      types: [{
        name: this.translateService.instant('recruitment.popup.licenses_types_groups.license_category_c_and_d.license_c'),
        value: 'c'
      }, {
        name: this.translateService.instant('recruitment.popup.licenses_types_groups.license_category_c_and_d.license_c1'),
        value: 'c1'
      }, {
        name: this.translateService.instant('recruitment.popup.licenses_types_groups.license_category_c_and_d.license_c1e'),
        value: 'c1e'
      }, {
        name: this.translateService.instant('recruitment.popup.licenses_types_groups.license_category_c_and_d.license_d'),
        value: 'd'
      }]
    }, {
      name: this.translateService.instant('recruitment.popup.licenses_types_groups.license_category_e.title'),
      types: [{
        name: this.translateService.instant('recruitment.popup.licenses_types_groups.license_category_e.license_be'),
        value: 'be'
      }, {
        name: this.translateService.instant('recruitment.popup.licenses_types_groups.license_category_e.license_ce'),
        value: 'ce'
      }, {
        name: this.translateService.instant('recruitment.popup.licenses_types_groups.license_category_e.license_de'),
        value: 'de'
      }]
    }];
  }

  closeDialog() {
    this.dialogRef.close();
  }

  public showConnexionDialog() {
    this.closeDialog();
    this.dialogService.open(PvRecruitmentDialogReactivateAccountComponent, {});
    this.recruitmentService.gaPageEvent('login-dialog');
  }

  updateCandidate() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
    } else if (this.form.get('drivingLicensePoints').value > 12) {
      this.toastService.showErrorToasts(
        this.translateService.instant('recruitment.popup.forms.error_license_points_max')
      );
    } else if (this.form.valid) {
      const candidate = this.generateCandidateObject()
      this.recruitmentService.updateCandidateEligibility(candidate).then(dataCandidate => {
        this.candidate = dataCandidate;
        this.eligibility = dataCandidate.eligibilityStatus;
        setTimeout(() => {
          this.dialogStep = this.dialogStep + 1;
          this.sendEligibleEvent();
        });
      })
    }

    if (!(this.form.get('hasAutoentrepreneurStatus') as UntypedFormControl).valid) {
      this.toastService.showErrorToasts(
        this.translateService.instant('recruitment.popup.forms.error_auto_entrepreneur')
      );
    }

    if (!(this.form.get('hasInsurer') as UntypedFormControl).valid) {
      this.toastService.showErrorToasts(
        this.translateService.instant('recruitment.popup.forms.error_has_insurer')
      );
    }

    if (!(this.form.get('drivingLicensePoints') as UntypedFormControl).valid) {
      this.toastService.showErrorToasts(
        this.translateService.instant('recruitment.popup.forms.error_license_points')
      );
    }

    if (!(this.form.get('drivingLicenseAge') as UntypedFormControl).valid) {
      this.toastService.showErrorToasts(
        this.translateService.instant('recruitment.popup.forms.error_license_age')
      );
    }

    if (!(this.form.get('drivingLicenseType') as UntypedFormControl).valid) {
      this.toastService.showErrorToasts(
        this.translateService.instant('recruitment.popup.forms.error_license_type')
      );
    }

    if (!(this.form.get('availableOnSaturday') as UntypedFormControl).valid) {
      this.toastService.showErrorToasts(
        this.translateService.instant('recruitment.popup.forms.error_available_on_saturday')
      );
    }
  }

  showAutoentrepreneurCreationMessage () {
    return !this.candidate.hasAutoentrepreneurStatus &&
      this.candidate.drivingLicenseAge === DrivingLicenseAge.moreThanOrEqual5 &&
      this.candidate.drivingLicensePoints >= 8 &&
      this.candidate.drivingLicenseType === DrivingLicenceType.european;
  }

  showEligibilityInvalidMessage () {
    return (this.candidate.hasAutoentrepreneurStatus || !this.candidate.hasAutoentrepreneurStatus) &&
      (this.candidate.drivingLicenseAge === DrivingLicenseAge.lessThan5 ||
      this.candidate.drivingLicensePoints < 8 ||
      this.candidate.drivingLicenseType === DrivingLicenceType.foreign);
  }

  restartRecruitmentProcess() {
    window.location.reload()
  }

  checkLicencePoints() {
    if(this.form.get('drivingLicensePoints').value > 12) {
      this.form.get('drivingLicensePoints').setValue('12');
    }
  }

  private generateCandidateObject() {
    const candidateSecondStepFormValues = this.form.getRawValue();

    const candidate: any = {
      hasAutoentrepreneurStatus: candidateSecondStepFormValues.hasAutoentrepreneurStatus,
      hasInsurer: candidateSecondStepFormValues.hasInsurer,
      noTVA: candidateSecondStepFormValues.noTVA,
      siret: candidateSecondStepFormValues.siret,
      drivingLicensePoints: candidateSecondStepFormValues.drivingLicensePoints,
      drivingLicenseAge: candidateSecondStepFormValues.drivingLicenseAge,
      drivingLicenseType: candidateSecondStepFormValues.drivingLicenseType
    };

    return candidate;
  }

  private sendEligibleEvent() {
    dataLayer.push({
      event: 'eligibility'
    });
  }

  getFormControlStatus(formControl: AbstractControl) {
    return !formControl.valid && !formControl.untouched ? 'danger' : '';
  }

  resetFile() {
    if (this.file) {
      this.file = null;
      this.fileName = '';
    }
  }

  fileInputChanged(file: any) {
    if (file) {
      this.file = file;
      this.fileName = file.name;
    }
  }

  public uploadFile() {
    const document: ValetDocument = {
      url: this.file,
      documentType: VALET_DOCUMENT_TYPE.insurance
    };

    this.isSubmitting = true;
    this.valetDocumentService.updateValetDocument(document).then(res => {
      this.isSubmitting = false;
    }).catch(err => {
      console.log('error update document')
    });
  }

  public redirectToQonto() {
    this.openQontoDialog(this.candidateUser)
  }

  public openQontoDialog(candidateUser: User) {
    const dialog = this.dialogService.open(PvRecruitmentQontoDialogComponent, {
      context: {
        candidateUser,
        retry: true
      }
    });
    return dialog.componentRef.instance.submitEvent.pipe(
      map(res => {
        return {
          dialog,
          data: res
        };
      })
    );
  }

  public redirectToPortailAutoEntrepreneur() {
    window.open('https://www.portail-autoentrepreneur.fr/lp/partenaires/popvalet', '_blank');
  }

  public redirectToOrusSubscribePage() {
    this.recruitmentService.redirectToOrusSubscribePage(null, this.form);
  }
}
